import './filter-hint.css';

let popupExtensionPromise = null;

// Первая загрузка групп через vueExtensionScriptTags не подключает сторонние rel.
// Загружаем окно вместе с его CSS и зависимостями штатным механизмом Битрикса.
function loadPopup() {
    if (!popupExtensionPromise) {
        popupExtensionPromise = BX.Runtime.loadExtension('main.popup').then((extension) => {
            const Popup = (extension && extension.Popup) || (BX.Main && BX.Main.Popup);
            if (typeof Popup !== 'function') {
                throw new Error('SAIT-59: main.popup не экспортирует Popup');
            }
            return Popup;
        }).catch((error) => {
            popupExtensionPromise = null;
            throw error;
        });
    }
    return popupExtensionPromise;
}

// SAIT-59: текст хранится в штатном FILTER_HINT; окно не участвует в выборе фильтра.
export const FilterHint = {
    props: {
        text: { type: String, default: '' },
        label: { type: String, default: '' },
        hintId: { type: String, required: true }
    },
    data() {
        return { opened: false, mobileHint: false };
    },
    created() {
        this.hintPopup = null;
        this.hintContent = null;
        this.hintCloseTimer = null;
        this.hintWanted = false;
        this.hintDisposed = false;
    },
    watch: {
        text() {
            this.closeHint();
        },
        hintId() {
            this.destroyHint();
        }
    },
    beforeUnmount() {
        this.hintDisposed = true;
        this.destroyHint();
    },
    methods: {
        async openHint(event) {
            // На мобильном фильтре (тот же порог 991px) окно не привязано к краю значка.
            // Нижняя панель через Teleport не расширяет документ и не сдвигает фильтры.
            if (window.matchMedia('(max-width: 991px)').matches) {
                if (event && event.type !== 'click') {
                    return;
                }
                this.closeHint();
                this.mobileHint = true;
                this.opened = true;
                window.addEventListener('resize', this.closeHint);
                this.$nextTick(() => {
                    if (this.mobileHint && this.$refs.mobileClose) {
                        this.$refs.mobileClose.focus({ preventScroll: true });
                    }
                });
                return;
            }
            this.cancelClose();
            this.hintWanted = true;
            if (this.opened) {
                return;
            }

            try {
                const Popup = await loadPopup();
                if (this.hintWanted && !this.hintDisposed) {
                    this.showHint(Popup);
                }
            } catch (error) {
                console.error('SAIT-59: не удалось открыть подсказку', error);
            }
        },
        showHint(Popup) {
            if (this.opened) {
                return;
            }
            if (!this.hintPopup) {
                const content = document.createElement('div');
                this.hintContent = content;
                content.id = this.hintId + '-text';
                content.setAttribute('role', 'tooltip');
                this.hintPopup = new Popup({
                    id: this.hintId,
                    bindElement: this.$refs.button,
                    content,
                    className: 'tas-filter-hint-popup',
                    autoHide: true,
                    closeByEsc: true,
                    offsetTop: 8,
                    padding: 12,
                    angle: true,
                    autoHideHandler: (event) => !this.$refs.button.contains(event.target)
                        && !this.hintPopup.getPopupContainer().contains(event.target),
                    events: {
                        onShow: () => {
                            this.opened = true;
                            document.addEventListener('scroll', this.onScroll, true);
                            window.addEventListener('resize', this.closeHint);
                        },
                        onClose: () => {
                            this.hintWanted = false;
                            this.opened = false;
                            this.unbindHint();
                        }
                    }
                });
                const popup = this.hintPopup.getPopupContainer();
                popup.addEventListener('mouseenter', this.cancelClose);
                popup.addEventListener('mouseleave', this.scheduleClose);
            }

            // textContent сохраняет текст подсказки и не исполняет HTML из свойства.
            this.hintContent.textContent = this.text.trim();
            this.hintPopup.setWidth(Math.min(320, window.innerWidth - 32));
            this.hintPopup.setMaxHeight(Math.min(280, window.innerHeight - 32));
            this.hintPopup.show();
        },
        cancelClose() {
            clearTimeout(this.hintCloseTimer);
            this.hintCloseTimer = null;
        },
        scheduleClose() {
            if (this.mobileHint) {
                return;
            }
            this.cancelClose();
            // Даём перевести курсор со значка на текст подсказки.
            this.hintCloseTimer = setTimeout(() => {
                const button = this.$refs.button;
                const popup = this.hintPopup && this.hintPopup.getPopupContainer();
                if ((button && (button.matches(':hover') || button === document.activeElement))
                    || (popup && popup.matches(':hover'))) {
                    return;
                }
                this.closeHint();
            }, 150);
        },
        closeHint() {
            const returnFocus = this.mobileHint && !this.hintDisposed;
            this.mobileHint = false;
            this.opened = false;
            this.hintWanted = false;
            this.cancelClose();
            this.unbindHint();
            if (this.hintPopup) {
                this.hintPopup.close();
            }
            if (returnFocus) {
                this.$nextTick(() => {
                    const button = this.$refs.button;
                    if (button && button.isConnected) {
                        button.focus({ preventScroll: true });
                    }
                });
            }
        },
        onScroll(event) {
            if (this.hintPopup && !this.hintPopup.getPopupContainer().contains(event.target)) {
                this.closeHint();
            }
        },
        unbindHint() {
            document.removeEventListener('scroll', this.onScroll, true);
            window.removeEventListener('resize', this.closeHint);
        },
        destroyHint() {
            this.closeHint();
            this.hintWanted = false;
            this.cancelClose();
            this.unbindHint();
            if (this.hintPopup) {
                const popup = this.hintPopup.getPopupContainer();
                popup.removeEventListener('mouseenter', this.cancelClose);
                popup.removeEventListener('mouseleave', this.scheduleClose);
                this.hintPopup.destroy();
                this.hintPopup = null;
            }
            this.hintContent = null;
            this.opened = false;
        }
    },
    template: `
        <button v-if="text && text.trim()" ref="button" type="button" class="tas-filter-hint"
            :aria-label="'Пояснение: ' + label" :aria-expanded="opened"
            :aria-controls="mobileHint ? hintId + '-mobile' : hintId"
            :aria-describedby="opened ? hintId + (mobileHint ? '-mobile-text' : '-text') : null"
            @mouseenter="openHint" @mouseleave="scheduleClose" @focus="openHint" @blur="scheduleClose"
            @click.stop="openHint" @keydown.esc.stop.prevent="closeHint" @keydown.tab="closeHint">
            <span aria-hidden="true">?</span>
        </button>
        <Teleport to="body">
            <div v-if="mobileHint" class="tas-filter-hint-mobile" @click.self="closeHint"
                @touchmove.self.prevent @keydown.esc.stop.prevent="closeHint"
                @keydown.tab.prevent="$refs.mobileClose.focus({ preventScroll: true })">
                <section :id="hintId + '-mobile'" class="tas-filter-hint-mobile__panel"
                    role="dialog" aria-modal="true" :aria-labelledby="hintId + '-mobile-title'"
                    :aria-describedby="hintId + '-mobile-text'">
                    <div class="tas-filter-hint-mobile__head">
                        <div :id="hintId + '-mobile-title'" class="tas-filter-hint-mobile__title">{{ label }}</div>
                        <button ref="mobileClose" type="button" class="tas-filter-hint-mobile__close"
                            aria-label="Закрыть пояснение" @click.stop="closeHint">
                            <svg class="icon icon-close" aria-hidden="true"><use xlink:href="#icon-close"></use></svg>
                        </button>
                    </div>
                    <div :id="hintId + '-mobile-text'" class="tas-filter-hint-mobile__text">{{ text.trim() }}</div>
                </section>
            </div>
        </Teleport>
    `
};
